import {
	isTabbedOperationTool,
	type OperationToolKey,
	type OpsTier,
	TABBED_OPERATION_TOOLS,
} from "@elaso/shared/types/operation-tools";
import {
	CalendarDays,
	FileText,
	Hash,
	LayoutDashboard,
	Link2,
	type LucideIcon,
	Shield,
	UserCheck,
	Users,
} from "lucide-react";
import { useStore } from "zustand";
import { coreStore } from "@/lib/stores/core-store";
import type { OperationsRoutes } from "@/operations-routes";

/**
 * Operations access, the client-side view over the shared operation-tool
 * registry in `@elaso/shared/types/operation-tools`, which the server seed and
 * gate read too. This module only adds the UI presentation, icon and label, and
 * the visibility hooks over the server-sent grant payload.
 *
 * Nothing here names a role, and nothing here may (TECH-3239). Role names are
 * one tenant's vocabulary, owned by its adapter. The client learns what the
 * caller may do from `operations.getAccessibleTools`, the same grant the gate
 * enforces rather than a guess that has to be kept in step with it.
 */

/** UI presentation for a tool that has an `/operations` tab. */
export type OpsToolDef = {
	key: OperationToolKey;
	type: OperationsRoutes["type"];
	label: string;
	icon: LucideIcon;
	tier: OpsTier;
};

/** Icon and label per tool key. Keys with a `/operations` route appear in OPS_TOOLS. */
const TOOL_PRESENTATION: Partial<
	Record<OperationToolKey, { label: string; icon: LucideIcon }>
> = {
	dashboard: { label: "Program dashboard", icon: LayoutDashboard },
	users: { label: "User management", icon: Users },
	channels: { label: "Channel management", icon: Hash },
	attendance: { label: "Attendance tracker", icon: UserCheck },
	scheduler: { label: "Studio scheduler", icon: CalendarDays },
	roles: { label: "Role management", icon: Shield },
	documents: { label: "Document management", icon: FileText },
	invites: { label: "Invite links", icon: Link2 },
};

/**
 * Every operations tool that has a `/operations` tab, in sidebar order, with its
 * access tier. Derived from the shared tabbed set and the UI presentation map
 * above, and the SAME list `operations.listWorkspaces` filters on, so what the
 * portal offers and what it can open cannot drift (TECH-3284).
 */
export const OPS_TOOLS: OpsToolDef[] = TABBED_OPERATION_TOOLS.flatMap(
	(tool) => {
		const presentation = TOOL_PRESENTATION[tool.key];
		if (!(tool.route && presentation)) return [];
		return [
			{
				key: tool.key,
				type: tool.route as OperationsRoutes["type"],
				label: presentation.label,
				icon: presentation.icon,
				tier: tool.readTier,
			},
		];
	}
);

/**
 * The tools the current user may open, in sidebar order. Drives both the ops
 * sidebar, which renders them, and the `$tool` deep link, which checks whether a
 * tool is allowed. Order matches {@link OPS_TOOLS}.
 *
 * Server-driven (docs/adr/27-the-operations-client.md). The set comes from
 * `operations.getAccessibleTools`, cached in core-store, the data-backed
 * successor to the old role-name tier check. The `/operations` route guard
 * fetches it on entry and on workspace change.
 */
export function useVisibleOpsTools(): OpsToolDef[] {
	const accessible = useStore(coreStore, (s) => s.accessibleOpsTools);
	const allowed = new Set(accessible.map((t) => t.toolKey));
	return OPS_TOOLS.filter((tool) => allowed.has(tool.key));
}

/**
 * Whether the caller may open the portal in the CURRENT workspace, the route's
 * entry gate.
 *
 * Tabbed tools only (TECH-3284). `operations.getAccessibleTools` reports all
 * eleven, orphans included, because `/app` reads the `broadcast` one for its
 * broadcast controls, but a grant on an orphan alone would admit a user to a
 * portal whose sidebar is empty. Sharing `isTabbedOperationTool` with
 * `operations.listWorkspaces` is what makes "listed" and "enterable" the same
 * answer.
 */
export function useHasOpsAccess(): boolean {
	return useStore(coreStore, (s) =>
		s.accessibleOpsTools.some((tool) => isTabbedOperationTool(tool.toolKey))
	);
}

/**
 * Whether the caller may mutate through one tool in the current workspace. This
 * is the same `moderate` grant `.requireToolAccess(key, "moderate")` enforces
 * server-side, so a control hidden here is one the procedure behind it would
 * refuse.
 *
 * False until the payload lands, and false again while it is cleared on a
 * workspace switch. A control that appears a moment late is a smaller problem
 * than one that appears for a user who may not use it.
 *
 * Callable from either client, but only where the payload is fetched,
 * `/operations` on route entry and `/app` in its load chain.
 */
export function useCanMutateOpsTool(key: OperationToolKey): boolean {
	return useStore(coreStore, (s) =>
		s.accessibleOpsTools.some((tool) => tool.toolKey === key && tool.canMutate)
	);
}
